import { useState } from 'react';
import { EntityLink } from '@/components/nav/EntityLink';
import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldControl, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, Td, Th } from '@/components/ui/table';
import { Toggle } from '@/components/ui/toggle';
import { ApiError, api, type ProjectType } from '@/lib/api';
import { toProjects } from '@/lib/nav';
import { useApi } from '@/lib/useApi';

/**
 * プロジェクト種別の管理。
 *
 * 種別は単なるラベルではなく、工数の数え方を 2 つ決める。
 *   請求可能の既定 : この種別のプロジェクトに記録した工数の isBillable 初期値
 *   直接工数       : 稼働レポートで「開発」を直接工数として数えるか
 * どちらも記録・集計の意味に直結するので、変更できるのは管理者だけ。
 *
 * 使用中の種別は消せない (API が 409 を返す)。使わなくなったものは無効化して
 * 選択肢から外す。無効化しても、既にその種別が付いているプロジェクトの
 * 表示は変わらない。
 */
export function ProjectTypeSettings({ isAdmin }: { isAdmin: boolean }) {
  const { data, error, loading, reload } = useApi<{ projectTypes: ProjectType[] }>(
    '/api/project-types',
  );
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, note: string) {
    setMessage(null);
    try {
      await action();
      setMessage(note);
      reload();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    }
  }

  return (
    <section className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
      <h2 className="text-sm font-bold">プロジェクト種別</h2>
      <p className="text-xs leading-relaxed text-ink-subtle">
        プロジェクトの分類と、その工数の数え方を決めます。
        <span className="text-ink-secondary">請求可能</span>{' '}
        はこの種別に記録した工数を既定で請求可能として扱うか、
        <span className="text-ink-secondary">直接工数</span>{' '}
        は稼働レポートで「開発」を直接工数として数えるかです。
        資産化の可否は種別ではなくプロジェクトごとに設定します。
      </p>

      {/* 保存後の再読み込みで表ごと消えないよう、初回だけ読み込み表示にする */}
      <StateBlock loading={loading && data === null} error={error}>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>TYPE</Th>
                <Th>請求可能</Th>
                <Th>直接工数</Th>
                <Th className="text-right">PROJECTS</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(data?.projectTypes ?? []).map((type) => (
                <TypeRow key={type.id} type={type} isAdmin={isAdmin} onRun={run} />
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </StateBlock>

      {isAdmin ? <CreateRow onRun={run} /> : null}
      {message ? <p className="text-xs text-ink-muted">{message}</p> : null}
    </section>
  );
}

type Runner = (action: () => Promise<unknown>, note: string) => Promise<void>;

function TypeRow({ type, isAdmin, onRun }: { type: ProjectType; isAdmin: boolean; onRun: Runner }) {
  const [name, setName] = useState(type.name);
  const archived = type.status === 'archived';
  const dirty = name.trim() !== type.name;

  function patch(body: Record<string, unknown>, note: string) {
    return onRun(() => api.patch(`/api/project-types/${type.id}`, body), note);
  }

  return (
    <tr>
      <Td>
        {isAdmin ? (
          <div className="flex items-center gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-56"
              aria-label="種別名"
            />
            {dirty ? (
              <Button onClick={() => patch({ name: name.trim() }, '名前を変更しました')}>
                保存
              </Button>
            ) : null}
            {archived ? <Badge variant="outline">無効</Badge> : null}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-medium">{type.name}</span>
            {archived ? <Badge variant="outline">無効</Badge> : null}
          </div>
        )}
      </Td>
      <Td>
        <Toggle
          checked={type.defaultBillable}
          onChange={(value) => patch({ defaultBillable: value }, '保存しました')}
          label={`${type.name} の工数を既定で請求可能にする`}
          disabled={!isAdmin}
        />
      </Td>
      <Td>
        <Toggle
          checked={type.countsAsDirect}
          onChange={(value) => patch({ countsAsDirect: value }, '保存しました')}
          label={`${type.name} の開発を直接工数として数える`}
          disabled={!isAdmin}
        />
      </Td>
      {/* 「この種別を使っているのはどれか」はここからしか辿れない。
          0 件のときはリンク先が空になるだけなので数字のまま出す。 */}
      <Td className="text-right font-mono text-ink-muted">
        {type.projectCount === 0 ? (
          type.projectCount
        ) : (
          <EntityLink target={toProjects(type.name)}>{type.projectCount}</EntityLink>
        )}
      </Td>
      <Td className="text-right">
        {isAdmin ? (
          <div className="flex justify-end gap-2">
            <Button
              onClick={() =>
                patch(
                  { status: archived ? 'active' : 'archived' },
                  archived ? '有効にしました' : '無効にしました',
                )
              }
            >
              {archived ? '有効化' : '無効化'}
            </Button>
            {/* 使用中は API が 409 で止めるが、押せる形にしておく必要が無いので隠す */}
            {type.projectCount === 0 ? (
              <Button
                variant="ghost"
                onClick={() =>
                  onRun(() => api.delete(`/api/project-types/${type.id}`), '削除しました')
                }
              >
                削除
              </Button>
            ) : null}
          </div>
        ) : null}
      </Td>
    </tr>
  );
}

function CreateRow({ onRun }: { onRun: Runner }) {
  const [name, setName] = useState('');
  const [defaultBillable, setDefaultBillable] = useState(true);
  const [countsAsDirect, setCountsAsDirect] = useState(true);

  return (
    <FieldRow className="border-t border-line pt-4">
      <Field label="種別を追加" className="min-w-64 flex-1">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="例: 保守運用契約"
        />
      </Field>
      <Field label="請求可能">
        <FieldControl>
          <Toggle checked={defaultBillable} onChange={setDefaultBillable} label="請求可能" />
        </FieldControl>
      </Field>
      <Field label="直接工数">
        <FieldControl>
          <Toggle checked={countsAsDirect} onChange={setCountsAsDirect} label="直接工数" />
        </FieldControl>
      </Field>
      <Button
        variant="primary"
        disabled={name.trim() === ''}
        onClick={() =>
          onRun(async () => {
            await api.post('/api/project-types', {
              name: name.trim(),
              defaultBillable,
              countsAsDirect,
            });
            setName('');
          }, '追加しました')
        }
      >
        追加
      </Button>
    </FieldRow>
  );
}
